import sampleSize from "lodash/sampleSize";
import { GetStaticPaths, GetStaticProps } from "next";
import { ComponentProps } from "react";
import { makeContentHtml } from "@/components/Article/Content/makeContentHtml";
import { extractTableOfContent } from "@/components/TableOfContent/extractTableOfContent";
import { Slug } from "@/components/kdotdev/slug/Slug";
import { buildClient } from "@/lib/contentful/client";
import { TechBlogFields } from "@/lib/contentful/contentModel/techBlog";
import { getTechBlogsOrderByDate } from "@/lib/contentful/getTechBlogsOrderByDate";
import { buildPreviewClient } from "@/lib/contentful/previewClient";
import { isNonNull } from "@/lib/guard";

export const getStaticPaths: GetStaticPaths = async () => {
  const client = buildClient();
  const { items } = await client.getEntries<TechBlogFields>({
    content_type: "techblog",
    select: ["fields.slug"],
    order: ["-sys.createdAt"],
    limit: 100,
  });

  const paths = items.map((item) => ({
    params: { slug: item.fields.slug },
  }));

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<ComponentProps<typeof Slug>> = async (context) => {
  const { params } = context;
  const client = buildClient();
  const previewClient = buildPreviewClient();

  if (typeof params?.slug !== "string") return { notFound: true };

  const { items } = await (context.preview ? previewClient : client).getEntries<TechBlogFields>({
    content_type: "techblog",
    "fields.slug": params.slug,
  });

  if (!items[0]) {
    return {
      notFound: true,
    };
  }
  const post = items[0];

  const { items: relatedPosts } = await getTechBlogsOrderByDate(client, 0, 6, {
    ["fields.tags.sys.id[in]" as string]: items[0].fields.tags?.filter(isNonNull).map((item) => item.sys.id),
    "sys.id[ne]": post.sys.id,
  });

  if (relatedPosts.length < 6) {
    const queryIds: string[] = [post.sys.id, ...relatedPosts.map((item) => item.sys.id)];
    const { items: posts } = await client.getEntries<TechBlogFields>({
      content_type: "techblog",
      "sys.id[nin]": queryIds,
      select: ["sys.id"],
    });
    const additionRelatedPostIds = sampleSize(posts, 6 - relatedPosts.length).map((item) => item.sys.id);

    const { items: additionRelatedPosts } = await client.getEntries<TechBlogFields>({
      content_type: "techblog",
      "sys.id[in]": additionRelatedPostIds,
      select: [
        "fields.tags",
        "fields.title",
        "fields.author",
        "fields.slug",
        "fields.image",
        "fields.importedAt",
        "sys.createdAt",
        "sys.id",
      ],
    });

    relatedPosts.push(...additionRelatedPosts);
  }

  const tableOfContent = extractTableOfContent(post.fields.body);

  const bodyHtml = post.fields.body && makeContentHtml(post.fields.body);

  return {
    props: {
      post,
      relatedPosts,
      tableOfContent,
      bodyHtml,
    },
  };
};

export default Slug;
